class SBCarousel {
  constructor(element) {
    if (!element) return;

    this.container = element;
    this.scrollContainer = this.container.querySelector('[data-scroll-container]');
    this.prevButton = this.container.querySelector('[data-arrow-prev]');
    this.nextButton = this.container.querySelector('[data-arrow-next]');
    this.paginationContainer = this.container.querySelector('[data-pagination-container]');
    
    this.dots = [];
    this.itemsPerPage = 1;
    this.totalPages = 0;
    this.currentPage = 0;
    this.isInitialized = false;
    this.itemObserver = null;
    this.mutationObserver = null; // New: For dynamic content
    this.debouncedResize = this.debounce(this.onResize.bind(this), 250);
    this.debouncedUpdate = this.debounce(this.handleContentChange.bind(this), 100);

    if (!this.scrollContainer || !this.prevButton || !this.nextButton) {
      return;
    }
  
    const visibilityObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isInitialized) {
            setTimeout(() => {
              this.init();
            }, 50);
            observer.unobserve(this.container);
          }
        });
      },
      { threshold: 0.01 }
    );

    visibilityObserver.observe(this.container);
  }

  // Get current items dynamically
  get items() {
    return Array.from(this.container.querySelectorAll('[data-carousel-item]'));
  }
  
  init() {
    if (this.isInitialized) return;
    
    this.isInitialized = true;
    this.bindEvents();
    this.setupMutationObserver(); // New: Watch for content changes
    this.updateCarousel(); // Initial setup
  }

  bindEvents() {
    this.nextButton.addEventListener('click', () => this.onArrowClick('next'));
    this.prevButton.addEventListener('click', () => this.onArrowClick('prev'));
    window.addEventListener('resize', this.debouncedResize);
    
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.refreshState();
      }
    });
  }

  // New: Setup mutation observer for dynamic content
  setupMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if carousel items were added or removed
          const addedItems = Array.from(mutation.addedNodes).some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            (node.matches('[data-carousel-item]') || node.querySelector('[data-carousel-item]'))
          );
          
          const removedItems = Array.from(mutation.removedNodes).some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            (node.matches('[data-carousel-item]') || node.querySelector('[data-carousel-item]'))
          );

          if (addedItems || removedItems) {
            shouldUpdate = true;
          }
        }
      });

      if (shouldUpdate) {
        this.debouncedUpdate();
      }
    });

    // Watch the scroll container and its children
    this.mutationObserver.observe(this.scrollContainer, {
      childList: true,
      subtree: true
    });
  }

  // New: Handle content changes
  handleContentChange() {
    if (!this.isInitialized) return;
    
    const oldItemCount = this.totalPages * this.itemsPerPage;
    const newItemCount = this.items.length;
    
    if (newItemCount === 0) {
      this.hideCarousel();
      return;
    }
    
    this.showCarousel();
    this.updateCarousel();
    
    // Adjust current page if items were removed
    if (newItemCount < oldItemCount) {
      const maxPage = Math.max(0, this.totalPages - 1);
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
        this.scrollToPage(this.currentPage, false);
      }
    }
  }

  // New: Complete carousel update
  updateCarousel() {
    this.calculatePageMetrics();
    this.setupPagination();
    this.setupItemObserver();
    
    // Ensure current page is valid
    this.currentPage = Math.max(0, Math.min(this.currentPage, this.totalPages - 1));
    this.updatePagination(this.currentPage);
    
    // Update button states
    this.updateButtonStates();
  }

  calculatePageMetrics() {
    const currentItems = this.items;
    
    if (currentItems.length > 0) {
      this.scrollContainer.offsetHeight; // Force layout
      
      const containerWidth = this.scrollContainer.clientWidth;
      const itemWidth = currentItems[0].offsetWidth || 1; 
      
      if (containerWidth > 0 && itemWidth > 0) {
        this.itemsPerPage = Math.max(1, Math.floor(containerWidth / itemWidth));
        this.totalPages = Math.ceil(currentItems.length / this.itemsPerPage);
      }
    } else {
      this.itemsPerPage = 1;
      this.totalPages = 0;
    }
  }

  setupPagination() {
    if (!this.paginationContainer) return;

    this.paginationContainer.innerHTML = '';
    this.dots = [];

    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.dataset.page = i;
      dot.addEventListener('click', () => this.onDotClick(i));
      this.paginationContainer.appendChild(dot);
      this.dots.push(dot);
    }
  }

  setupItemObserver() {
    if (this.itemObserver) {
      this.itemObserver.disconnect();
    }

    const currentItems = this.items;
    if (currentItems.length === 0) return;

    this.itemObserver = new IntersectionObserver((entries) => {
      if (!this.isCarouselVisible()) return;
      
      let mostVisibleItem = null;
      let maxVisibility = 0;

      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > maxVisibility) {
          maxVisibility = entry.intersectionRatio;
          mostVisibleItem = entry.target;
        }
      });

      if (mostVisibleItem) {
        const itemIndex = currentItems.indexOf(mostVisibleItem);
        if (itemIndex !== -1) { // Ensure item still exists
          const currentPage = Math.floor(itemIndex / this.itemsPerPage);
          
          if (currentPage !== this.currentPage && currentPage < this.totalPages) {
            this.currentPage = currentPage;
            this.updatePagination(currentPage);
          }
        }
      }
    }, { 
      root: this.scrollContainer, 
      threshold: [0.1, 0.5, 0.9]
    });

    currentItems.forEach(item => this.itemObserver.observe(item));
  }

  // New: Show/hide carousel based on content
  hideCarousel() {
    this.container.style.display = 'none';
    this.container.setAttribute('aria-hidden', 'true');
  }

  showCarousel() {
    this.container.style.display = '';
    this.container.removeAttribute('aria-hidden');
  }

  // New: Update button states
  updateButtonStates() {
    if (this.totalPages <= 1) {
      this.prevButton.style.display = 'none';
      this.nextButton.style.display = 'none';
    } else {
      this.prevButton.style.display = '';
      this.nextButton.style.display = '';
    }
  }

  isCarouselVisible() {
    const rect = this.container.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  onDotClick(pageIndex) {
    if (pageIndex >= 0 && pageIndex < this.totalPages) {
      this.currentPage = pageIndex;
      this.scrollToPage(pageIndex, true);
      this.updatePagination(pageIndex);
    }
  }

  scrollToPage(pageIndex, smooth = true) {
    const currentItems = this.items;
    const targetItemIndex = pageIndex * this.itemsPerPage;

    if (currentItems[targetItemIndex]) {
      let scrollTarget = currentItems[targetItemIndex].offsetLeft;

      if (pageIndex === 0) {
        scrollTarget = 0;
      }

      this.scrollContainer.scrollTo({
        left: scrollTarget,
        behavior: smooth ? 'smooth' : 'instant'
      });
    }
  }

  updatePagination(activePageIndex) {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activePageIndex);
      if (index === activePageIndex) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  onArrowClick(direction) {
    if (this.totalPages <= 1) return;

    let nextPageIndex;

    if (direction === 'next') {
      nextPageIndex = (this.currentPage + 1) % this.totalPages;
    } else {
      nextPageIndex = (this.currentPage - 1 + this.totalPages) % this.totalPages;
    }

    this.currentPage = nextPageIndex;
    this.scrollToPage(nextPageIndex, true);
    this.updatePagination(nextPageIndex);
  }

  onResize() {
    if (!this.isInitialized) return;
    
    const oldTotalPages = this.totalPages;
    this.calculatePageMetrics();

    if (oldTotalPages !== this.totalPages) {
      this.setupPagination();
      this.refreshState();
    }
  }

  refreshState() {
    if (!this.isInitialized || !this.isCarouselVisible()) return;
    
    this.scrollContainer.offsetHeight; // Force layout
    
    const currentItems = this.items;
    if (currentItems.length === 0) return;
    
    const currentItemIndex = this.getCurrentItemIndex();
    const currentPage = Math.floor(currentItemIndex / this.itemsPerPage);
    
    this.currentPage = Math.max(0, Math.min(currentPage, this.totalPages - 1));
    this.updatePagination(this.currentPage);
  }

  getCurrentItemIndex() {
    const currentItems = this.items;
    if (currentItems.length === 0) return 0;

    let closestIndex = 0;
    let minDistance = Infinity;
    const containerScrollLeft = this.scrollContainer.scrollLeft;

    currentItems.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - containerScrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });
    
    return closestIndex;
  }
  
  debounce(func, delay) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  // Enhanced cleanup method
  destroy() {
    if (this.itemObserver) {
      this.itemObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    window.removeEventListener('resize', this.debouncedResize);
  }

  // New: Public method to manually refresh after content changes
  refresh() {
    if (this.isInitialized) {
      this.handleContentChange();
    }
  }
}

// Rest of the initialization code remains the same...
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('[data-component="sb-carousel"]');
  const carouselInstances = [];
  
  carousels.forEach(carouselElement => {
    const instance = new SBCarousel(carouselElement);
    carouselInstances.push(instance);
  });

  // Handle tab changes
  document.addEventListener('click', (e) => {
    if (e.target.matches('[role="tab"], .tab-button, [data-tab]')) {
      setTimeout(() => {
        carouselInstances.forEach(instance => {
          if (instance.isInitialized) {
            instance.refreshState();
          }
        });
      }, 100);
    }
  });

  // Expose instances globally for manual refresh if needed
  window.carouselInstances = carouselInstances;
});
